/* eslint-disable @typescript-eslint/camelcase */
/**
 * Utility functions to initialise Google Maps, and to transform returned
 * Google Maps place objects into nicer objects that we can use directly in
 * our forms.
 *
 * We convert from Google Maps `place` object (`GoogleMapsPlace`), to an
 * interstitial and simplified address object (`PlaceAddress`), then can
 * convert that again to a very simplified form model version of the address,
 * which we can use directly in components which manage form state.
 */
import get from "lodash/get";
import { AppServiceGoogleMaps, AppConfig } from "../types";
import { AppStateLocal } from "../hooks/useAppState";
import debug from "./debug";
import appEvents from "./appEvents";
import {
  isArray,
  isObject,
  isEmpty,
  toArray,
  hasAndIsNotEmpty
} from "./helpers";
import { getStoredItem } from "./localStorage";

declare global {
  interface Window {
    initGoogleMaps?: Function;
  }
}

export interface PlaceAddressMap {
  [placeComponentKey: string]: string;
}

export interface PlaceAddress extends PlaceAddressMap {
  formattedAddress: string;
  name: string;
}

export type PlaceAddressToFormModelPart =
  | string
  | PlaceAddressToFormModelObject;

export type PlaceAddressToFormModelParts =
  | PlaceAddressToFormModelPart
  | PlaceAddressToFormModelPart[];

export interface PlaceAddressToFormModelObject {
  parts: PlaceAddressToFormModelParts;
  delimiter?: string | number;
  reverse?: boolean;
}

export interface PlaceAddressToFormModelMap {
  [formModelKey: string]: PlaceAddressToFormModelParts;
}

export interface AddressFormModel {
  [formModelKey: string]: string | null | undefined;
  streetAddress1: string;
  streetAddress2: string;
  addressLocality: string;
  addressRegion: string;
  postalCode: string;
  addressCountry: string;
}

/**
 * A list of countries which use maps.google.cn for the Google Maps API.
 */
export const COUNTRIES_USING_GOOGLE_MAPS_CN = ["CN", "HK", "MO"];

/**
 * This map transforms the Google Maps place object to a simplified
 * object that contains only the fields and values that we're interested in.
 */
export const PLACE_TYPE_TO_ADDRESS_MAPPING: PlaceAddressMap = {
  floor: "floor",
  country: "country",
  region: "administrative_area_level_1",
  administrative_area_level_2: "administrative_area_level_2",
  administrative_area_level_3: "administrative_area_level_3",
  locality: "locality",
  sublocality_level_4: "sublocality_level_4",
  sublocality_level_3: "sublocality_level_3",
  sublocality_level_2: "sublocality_level_2",
  sublocality_level_1: "sublocality_level_1",
  sublocality: "sublocality",
  postal_town: "postal_town",
  postal_code: "postal_code",
  postal_code_prefix: "postal_code_prefix",
  route: "route",
  street_number: "street_number",
  premise: "premise",
  subpremise: "subpremise",
  street_address1: "street_address1"
};

/**
 * These maps take transformed PlaceAddress objects and map them to
 * objects we will use in our internal app data models.
 */
export const ADDRESS_TO_MODEL_MAPPING: PlaceAddressToFormModelMap = {
  streetAddress1: ["street_number", "route", "premise", "subpremise"],
  streetAddress2: "name",
  addressLocality: ["locality", "postal_town"],
  addressRegion: "region",
  postalCode: ["postal_code_prefix", "postal_code"],
  addressCountry: "country"
};

export const REVERSE_STREET_NUMBER_ADDRESS_TO_MODEL_MAPPING: PlaceAddressToFormModelMap = {
  ...ADDRESS_TO_MODEL_MAPPING,
  streetAddress1: ["route", "street_number", "premise", "subpremise"]
};

export const CHINA_ADDRESS_TO_MODEL_MAPPING: PlaceAddressToFormModelMap = {
  // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
  streetAddress1: {
    parts: [["street_number", "route"], "premise", "neighbourhood"],
    delimiter: ", "
  } as PlaceAddressToFormModelParts,
  streetAddress2: "name",
  addressLocality: "sublocality_level_1",
  addressRegion: "region",
  postalCode: "postal_code",
  addressCountry: "country"
};

export const JAPAN_ADDRESS_TO_MODEL_MAPPING: PlaceAddressToFormModelMap = {
  streetAddress1: [
    {
      parts: [
        "sublocality_level_2",
        "sublocality_level_3",
        "sublocality_level_4",
        { parts: "premise", delimiter: " ", reverse: true }
      ],
      delimiter: "-",
      reverse: false
    },
    "floor"
  ],
  streetAddress2: "name",
  addressLocality: {
    parts: ["locality", "sublocality_level_1"],
    delimiter: ""
  },
  addressRegion: "region",
  postalCode: "postal_code",
  addressCountry: "country"
};

/*Values we retain from that list https://developers.google.com/places/supported_types*/
export const PLACE_NAME_TYPES: string[] = [
  "point_of_interest",
  "establishment",
  "colloquial_area",
  "establishment",
  "finance",
  "floor",
  "food",
  "general_contractor",
  "health",
  "intersection",
  "natural_feature",
  "neighborhood",
  "place_of_worship",
  "political",
  "point_of_interest",
  "post_box",
  "room",
  "store"
];

let GOOGLE_MAPS_INITIALISED: boolean = false;
let GOOGLE_MAPS_LANGUAGE: string | undefined;
const SCRIPT_ELEMENT_ID = "googleMapsScript";

/**
 * Initialise Google Maps in the app.
 */
export function initGoogleMaps({
  key,
  client,
  channel,
  libraries = "places",
  language,
  callback
}: AppServiceGoogleMaps) {
  // Prevent repeat initialisation
  if (GOOGLE_MAPS_INITIALISED) {
    debug.warn(
      "Google Maps was attempted to be re-initialised. It should only ever be initialised once."
    );
    // remove previous element (to reload in different language)
    let previousScriptElem = document.getElementById(SCRIPT_ELEMENT_ID);
    if (previousScriptElem) {
      previousScriptElem.remove();
    }
  }
  GOOGLE_MAPS_INITIALISED = true;
  GOOGLE_MAPS_LANGUAGE = language;

  // Dynamically build the URL with non-empty query params
  const urlParams: { [paramName: string]: string | undefined } = {
    key,
    client,
    channel,
    libraries,
    language
  };

  // Change the API URL based on the configured zone of the app
  const googleMapsApiUrl =
    getStoredItem("storeCountry") === "CN"
      ? "https://maps.google.cn"
      : "https://maps.googleapis.com";

  const loadGoogleMapsUrl = `${googleMapsApiUrl}/maps/api/js?${Object.keys(
    urlParams
  )
    .reduce(
      (acc: string[], paramName: string) =>
        !isEmpty(urlParams[paramName])
          ? acc.concat([
              `${paramName}=${encodeURIComponent(String(urlParams[paramName]))}`
            ])
          : acc,
      []
    )
    .join("&")}&callback=initGoogleMaps`;

  // Attach callback to window so it can fire after Google Maps initialises
  window.initGoogleMaps =
    callback ||
    function() {
      debug(
        `[googleMaps] Google Maps was loaded and initialised from ${googleMapsApiUrl} (${language})`
      );
    };

  // Append the Google Maps script to the document
  let loadGoogleMaps = document.createElement("script");
  loadGoogleMaps.src = loadGoogleMapsUrl;
  loadGoogleMaps.id = SCRIPT_ELEMENT_ID;
  document.body.appendChild(loadGoogleMaps);
}

/**
 * Extract the place address's part(s) value based on the model mapping.
 *
 * @NOTE I had lots of trouble thinking how to do the TS type stuff for this.
 * If there's a better way, please teach me sensei!
 */
export function extractAddressFormModelPartValues(
  address: PlaceAddress,
  part: PlaceAddressToFormModelParts,
  delimiter: string = " ",
  reverse: boolean = false
): string {
  let formattedPart: string | string[];

  // Array of multiple part names
  if (isArray(part)) {
    formattedPart = (part as PlaceAddressToFormModelPart[]).map(p =>
      extractAddressFormModelPartValues(address, p)
    );
  }
  // Object to mark out specific options when joining or reversing the part
  else if (isObject(part) && hasAndIsNotEmpty(part, "parts")) {
    formattedPart = extractAddressFormModelPartValues(
      address,
      get(part, "parts"),
      get(part, "delimiter"),
      get(part, "reverse")
    );
  }
  // Returns either string or array
  else {
    formattedPart = address[part as string];
  }

  if (isArray(formattedPart)) {
    formattedPart = ((reverse
      ? (formattedPart as string[]).reverse()
      : formattedPart) as string[])
      .filter((p: string | undefined) => p)
      .join(delimiter);
  }

  return formattedPart as string;
}

/**
 * Take the PlaceAddress model and convert it to the form model.
 */
export function convertAddressToFormModel(
  address: PlaceAddress,
  modelMap: PlaceAddressToFormModelMap
): AddressFormModel {
  const output: AddressFormModel = {
    streetAddress1: "",
    streetAddress2: "",
    addressLocality: "",
    addressRegion: "",
    postalCode: "",
    addressCountry: ""
  };

  Object.keys(modelMap).forEach((modelPartName: string) => {
    const modelPartMap = modelMap[modelPartName];
    output[modelPartName] = extractAddressFormModelPartValues(
      address,
      modelPartMap
    );
  });

  return output;
}

/**
 * Get a specific address component in the Google Maps Place object
 * by `type`.
 *
 * This searches all the `address_components` and matches on the
 * `type` value.
 *
 * You can also affect the value by specifying either `short_name`
 * or `long_name`. Leaving it blank will do it automatically for you.
 */
export function getPlaceValueByType(
  place: google.maps.places.PlaceResult,
  componentType: string,
  componentName?: "long_name" | "short_name"
): string | string[] {
  if (!place || !place.address_components) {
    return "";
  }
  const _componentName = !componentName
    ? componentType === "country" ||
      componentType === "administrative_area_level_1"
      ? "short_name"
      : "long_name"
    : componentName;
  const components = place.address_components.filter(c =>
    c.types.includes(componentType)
  );
  return !components.length
    ? // No matched parts, return empty
      ""
    : components.length > 1
    ? // Matched multiple parts with the same name, so output as array
      components.map(c => c[_componentName])
    : // Matched single part, output as string
      components[0][_componentName];
}

/** Check if the place name types are accepted values */
export function checkIfValidNameType(name: string[]) {
  return name.every(value => {
    return PLACE_NAME_TYPES.includes(value);
  });
}

/**
 * This maps a large Google Maps place object, to a shorter abbreviated
 * object (called a Place Address).
 *
 * This will pluck only the primary components that we're interested in
 * and will map the names
 */
export function convertPlaceToAddress(place: google.maps.places.PlaceResult) {
  const address: PlaceAddress = Object.keys(
    PLACE_TYPE_TO_ADDRESS_MAPPING
  ).reduce(
    (acc, inputName) => {
      const _type = PLACE_TYPE_TO_ADDRESS_MAPPING[inputName];
      const partValue = getPlaceValueByType(place, _type);
      const currentPartValue = get(acc, inputName);
      const newPartValue = currentPartValue
        ? toArray(currentPartValue).push(partValue)
        : partValue;
      return { ...acc, [inputName]: newPartValue };
    },
    {
      formattedAddress: "",
      name: place.types && checkIfValidNameType(place.types) ? place.name : ""
    }
  );

  // This part is important, due to Google giving us a localised formatted version of the
  // whole address. In the case of Japanese and Chinese addresses, it's country -> street,
  // for others it's street -> country. More importantly, using this direction we can
  // pluck the formatted street line from whichever end and display that, rather than
  // clumsily piecing it together from the address_components.
  address.formattedAddress = place.formatted_address || "";

  // Make the name empty if it matches the same value as another property
  // When searching for places by postcode, the postcode would be returned
  // in the postal_code field, and as the name. It looked weird when
  // inserted into the form...
  for (let propName in address) {
    if (
      address.hasOwnProperty(propName) &&
      propName !== "name" &&
      address[propName] === address.name
    ) {
      address.name = "";
      break;
    }
  }

  return address;
}

/**
 * Does what it says on the tin!
 *
 * This automates choosing which model map to use so you can give it a Google
 * Maps place object and it will transform using the appropriate form model map.
 */
export function convertPlaceToFormModel(
  place: google.maps.places.PlaceResult
): AddressFormModel {
  debug("[googleMaps] convertPlaceToFormModel: place:", place);
  const address = convertPlaceToAddress(place);

  // adjust address fields
  if (address.postal_code === address.postal_code_prefix) {
    address.postal_code_prefix = "";
  }
  debug("[googleMaps] convertPlaceToFormModel: address:", address);

  let formModel;
  switch (address.country) {
    case "JP":
      formModel = convertAddressToFormModel(
        address,
        JAPAN_ADDRESS_TO_MODEL_MAPPING
      );
      // Japanese street addresses are the last item of the formatted address

      // formModel.streetAddress1 =
      //   address.formattedAddress.split(/,\s*/).pop() || "";
      break;

    case "CN":
      formModel = convertAddressToFormModel(
        address,
        CHINA_ADDRESS_TO_MODEL_MAPPING
      );
      // Chinese street addresses are the last item of the formatted address
      // formModel.streetAddress1 =
      //   address.formattedAddress.split(/,\s*/).pop() || "";
      break;

    default:
      formModel = convertAddressToFormModel(address, ADDRESS_TO_MODEL_MAPPING);

      // Most other countries street addresses are the first item of the formatted address
      formModel.streetAddress1 = toArray(
        address.formattedAddress,
        ", "
      ).shift();
      // check if formatted address was without street
      // and the first part is city and postal code
      if (
        formModel.streetAddress1 ===
        formModel.addressLocality + " " + formModel.postalCode
      ) {
        formModel.streetAddress1 = "";
      }
      break;
  }

  // Remove the second line if it is the same as the first line
  if (formModel.streetAddress1 === formModel.streetAddress2) {
    formModel.streetAddress2 = "";
  }
  debug("[googleMaps] convertPlaceToFormModel: formModel:", formModel);
  return formModel;
}

export function bootstrap(config: AppConfig, initialLanguage: string) {
  let handler = ({
    appState,
    prevAppState
  }: {
    appState: AppStateLocal;
    prevAppState: AppStateLocal;
  }) => {
    //debug("[googleMaps] bootstrap", { appState, prevAppState });
    let currentLanguage = get(appState, "language");

    if (!GOOGLE_MAPS_INITIALISED || currentLanguage !== GOOGLE_MAPS_LANGUAGE) {
      if (hasAndIsNotEmpty(config, "services.googleMaps")) {
        let googleMapsConfig: AppServiceGoogleMaps = {
          ...get(config, "services.googleMaps"),
          language: currentLanguage || initialLanguage
        };
        initGoogleMaps(googleMapsConfig);
      }
    } else {
      // If already been initialised, check to see if the store country has changed
      // Reload the page if the app's store country requires maps.google.cn so Google Maps can
      // re-initialise with the correct domain.
      if (
        appState.storeCountry !== prevAppState.storeCountry &&
        (COUNTRIES_USING_GOOGLE_MAPS_CN.includes(appState.storeCountry) ||
          COUNTRIES_USING_GOOGLE_MAPS_CN.includes(prevAppState.storeCountry))
      ) {
        window.location.reload();
      }
    }
  };
  appEvents.on("app.initialised", handler);
  // handle switch language
  appEvents.on("app.setup", handler);
}
